import { useEffect, useRef } from 'react';

/**
 * A single tasteful hero parallax effect (owner-approved "moderate motion"). Moves the ref'd element
 * at a fraction of scroll speed via a transform (GPU-cheap, no layout thrash), throttled to one
 * rAF per frame. Fully disabled under `prefers-reduced-motion: reduce` — returns a static ref with
 * no listener attached, so reduced-motion users get a plain, still hero.
 */
export function useParallax(speed = 0.25, scale = 1.12) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    let ticking = false;
    const apply = () => {
      const rect = el.getBoundingClientRect();
      // Skip the write once the hero has scrolled fully off-screen — the transform would be
      // invisible anyway (element out of view), so this just avoids a wasted layout read + style
      // write on every scroll frame for the rest of the page's life.
      if (rect.bottom < 0) { ticking = false; return; }
      const offset = Math.max(0, -rect.top) * speed;
      // `scale` must match the CSS baseline (JobsHero.css .jhero-bg) — this write REPLACES the
      // whole transform, so omitting scale here would snap the image back to its unscaled size
      // (re-exposing the edge gap the scale exists to prevent) the instant this effect runs.
      el.style.transform = `translate3d(0, ${offset}px, 0) scale(${scale})`;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [speed, scale]);

  return ref;
}
